/**
 * PaletteDrawer — the floating palette's expanded picker (TV1-2291 · S18).
 *
 * The PRESENTER half of `WidgetToolbar`: it owns no filtering, and receives groups
 * that are already narrowed by the active source and the search box.
 *
 * S18 replaced two things here:
 *
 *  - the side nav's four hard-coded categories (two of which could never contain a
 *    widget) with the data sources actually present in `groups`;
 *  - the card body, which rendered the RAW TYPE STRING as both the label and the
 *    description, under a `// TODO: hacer description para cada widget`. Both now
 *    come from `widgetCatalog`.
 */

import {
  ActionIcon, Box, Card, Group, NavLink, ScrollArea, SegmentedControl, SimpleGrid, Text, TextInput, Tooltip
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconLayoutGridAdd, IconSearch, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import { WidgetDefinition, WidgetType } from '../../../Widgets/types';
import { widgetGlyph } from '../../../Widgets/WidgetIcons/glyphs';
import {
  widgetDescriptionFallback,
  widgetDescriptionKey,
  widgetLabelFallback,
  widgetLabelKey,
  widgetSourceLabelFallback,
  widgetSourceLabelKey,
  type WidgetDataSource,
  type WidgetSourceGroup
} from '../../../widgetCatalog';
import type { WidgetPaletteFilter } from '../WidgetToolbar';
import classes from './PaletteDrawer.module.css';

type Props = {
  opened: boolean;
  onClose: () => void;
  /** Sources with at least one available type — the nav is built from these. */
  sources: WidgetDataSource[];
  activeFilter: WidgetPaletteFilter;
  onFilterChange: ( filter: WidgetPaletteFilter ) => void;
  search: string;
  onSearch: ( value: string ) => void;
  /** Already filtered by `activeFilter` + `search`. Empty groups never appear. */
  groups: WidgetSourceGroup[];
  onAddWidget: ( widgetDefinition: Partial<WidgetDefinition> ) => void;
};

export default function PaletteDrawer({
  opened, onClose, sources, activeFilter, onFilterChange, search, onSearch, groups, onAddWidget
}: Props ) {
  const { t } = useTranslation( 'widgets' );
  const isMobile = useMediaQuery( '(max-width: 48em)' );
  const gridCols = isMobile ? 1 : { base: 3, sm: 4, lg: 5 };

  const sourceLabel = ( source: WidgetDataSource ): string =>
    t( widgetSourceLabelKey( source ), widgetSourceLabelFallback( source ));

  // `all` first, then one entry per source that actually has members.
  const filterOptions: { label: string; value: WidgetPaletteFilter }[] = [
    { label: t( 'palette_drawer_source_all', 'Todos' ), value: 'all' },
    ...sources.map(( source ) => ({ label: sourceLabel( source ), value: source as WidgetPaletteFilter }))
  ];

  const handleWidgetClick = ( widgetType: WidgetType ) => {
    onAddWidget({ type: widgetType });
    onClose();
  };

  if ( !opened ) return null;

  /*
   * ONE icon set across both palette surfaces (2026-08-19, ticket owner: "use the
   * icons proposed for this and the extended full menu").
   *
   * These were Mantine `<Image>` tags pointing at 600×600 SVG files that paint
   * themselves `#4c6ef5` — a literal in the markup. That is the wrong hue (the
   * app's accent is teal), the wrong value in dark mode, and unreachable from CSS,
   * so the tile could never tint its glyph on hover. The inline glyphs are
   * `currentColor`, which makes all three go away and lets `.widgetPreview`'s own
   * `color` drive them. See `WidgetIcons/glyphs.tsx`.
   */

  /*
   * Two DIFFERENT empty states (design-system.md "Empty States"): "this report
   * accepts no card type at all" is an entirely different problem from "your search
   * matched nothing", and one message for both would send the user looking in the
   * wrong place.
   */
  const emptyState = sources.length === 0
    ? {
      title: t( 'palette_drawer_unavailable_title', 'No hay tarjetas disponibles' ),
      description: t(
        'palette_drawer_unavailable_description',
        'Este informe no admite ningún tipo de tarjeta para el dispositivo elegido.'
      )
    }
    : {
      title: t( 'palette_drawer_empty_title', 'Sin resultados' ),
      description: t(
        'palette_drawer_empty_description',
        'Ningún tipo de tarjeta coincide con la búsqueda.'
      )
    };

  return (
    <Box className={classes.drawerRoot} onClick={onClose}>
      <div
        className={classes.drawerPanel}
        onClick={( e ) => e.stopPropagation()}
      >
        {!isMobile && (
          <aside className={classes.drawerSide}>
            <TextInput
              placeholder={t( 'palette_drawer_search_input', 'Buscar widgets' )}
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={( e ) => onSearch( e.currentTarget.value )}
              mb="md"
            />

            <ScrollArea.Autosize mah="calc(100vh - 150px)" offsetScrollbars>
              {filterOptions.map(( option ) => (
                <NavLink
                  key={option.value}
                  active={option.value === activeFilter}
                  label={option.label}
                  onClick={() => onFilterChange( option.value )}
                />
              ))}
            </ScrollArea.Autosize>
          </aside>
        )}

        <ScrollArea className={classes.drawerContent}>
          <div className={classes.contentInner}>
            <Group justify="flex-end" mb="md">
              <ActionIcon
                size="lg"
                radius="xl"
                variant="subtle"
                color="gray"
                onClick={onClose}
                aria-label={t( 'palette_drawer_close', 'Cerrar la paleta' )}
              >
                <IconX size={18} />
              </ActionIcon>
            </Group>

            {isMobile && (
              <div className={classes.mobileHeader}>
                <TextInput
                  placeholder={t( 'palette_drawer_search_input', 'Buscar widgets' )}
                  leftSection={<IconSearch size={16} />}
                  value={search}
                  onChange={( e ) => onSearch( e.currentTarget.value )}
                />
                <SegmentedControl
                  fullWidth
                  size="xs"
                  value={activeFilter}
                  data={filterOptions}
                  onChange={( value ) => onFilterChange( value as WidgetPaletteFilter )}
                  radius="md"
                />
              </div>
            )}

            {groups.length === 0 ? (
              <div className={classes.emptyState}>
                <div className={classes.emptyStateIcon}>
                  <IconLayoutGridAdd size={20} />
                </div>
                <Text className={classes.emptyStateTitle}>{emptyState.title}</Text>
                <Text className={classes.emptyStateDescription}>{emptyState.description}</Text>
              </div>
            ) : (
              groups.map(( group ) => (
                <section key={group.source} className={classes.sourceGroup}>
                  <Text component="h3" className={classes.sourceHeading}>
                    {sourceLabel( group.source )}
                  </Text>

                  <SimpleGrid cols={gridCols} spacing="md">
                    {group.types.map(( type ) => (
                      <Card
                        key={type}
                        shadow="sm"
                        radius="md"
                        withBorder
                        className={classes.widgetCard}
                        data-testid={componentsTestIds.reportsPaletteItem( type )}
                        onClick={() => handleWidgetClick( type )}
                      >
                        <div className={classes.widgetPreview}>
                          {widgetGlyph( type, 44 )}
                        </div>

                        <Group justify="space-between" wrap="nowrap" mt="sm">
                          <Text fw={500} size="sm">
                            {t( widgetLabelKey( type ), widgetLabelFallback( type ))}
                          </Text>
                          <Tooltip label={t( 'add_widget_tooltip', 'Añadir widget' )}>
                            <span className={classes.addHint}>+</span>
                          </Tooltip>
                        </Group>

                        <Text size="xs" c="dimmed" mt={4}>
                          {t( widgetDescriptionKey( type ), widgetDescriptionFallback( type ))}
                        </Text>
                      </Card>
                    ))}
                  </SimpleGrid>
                </section>
              ))
            )}
          </div>
        </ScrollArea>
      </div>
    </Box>
  );
}
